/**
 * Seep bot.
 *
 * Every legal play (a trail, each capture, each house it could build or increase) is valued by the points it wins
 * and what it gives away:
 *  - Captured cards are worth their Seep points, a seep is worth 50, and the 10 of diamonds and the spades are the
 *    prizes to hold.
 *  - Trailing a card costs the points it carries, and leaving a floor the next player could clear in one play is a
 *    risk; so is a house the opponents can capture. NORMAL and above count the cards they can see to estimate how
 *    likely the opponents are to hold the right card; weaker bots assume a flat chance.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { seepPoints, seepPointsOf, seepValue, type SeepAction, type SeepLegal, type SeepView } from './engine.js';

/** Chance that at least one of the opponents' cards has value `v`. */
function oppHolds(view: SeepView, v: number, ctx: BotContext): number {
  if (v < 1 || v > 13) return 0;
  if (ctx.profile.memory <= 0) return 0.3;
  const seen = new Set<CardId>([...view.hand, ...view.loose, ...view.houses.flatMap((h) => h.cards)]);
  const unseen = buildDeck().filter((c) => !seen.has(c));
  const matching = unseen.filter((c) => seepValue(c) === v).length;
  const mine = view.seat ?? 0;
  const oppCards = view.handCounts[(mine + 1) % 4]! + view.handCounts[(mine + 3) % 4]!;
  const n = Math.max(1, unseen.length);
  // Probability none of the opponents' cards match, treating the draws as independent.
  return Math.min(1, 1 - Math.pow(1 - matching / n, oppCards));
}

/** Chance the next opponent can clear a floor made only of loose cards (no house left, total at most 13). */
function seepRisk(view: SeepView, loose: readonly CardId[], houses: number, ctx: BotContext): number {
  if (loose.length === 0 || houses > 0) return 0;
  const sum = loose.reduce((n, c) => n + seepValue(c), 0);
  if (sum > 13) return 0;
  return oppHolds(view, sum, ctx);
}

interface Candidate {
  action: SeepAction;
  score: number;
}

function candidates(view: SeepView, legal: SeepLegal, ctx: BotContext): Candidate[] {
  const out: Candidate[] = [];
  const lastPlay = view.deckCount === 0 && view.handCounts.reduce((a, b) => a + b, 0) === 1;
  const gone = (taken: readonly CardId[]) => view.loose.filter((c) => !taken.includes(c));
  for (const m of legal.moves) {
    const own = seepPoints(m.card);
    if (m.trail) {
      const risk = seepRisk(view, [...view.loose, m.card], view.houses.length, ctx);
      out.push({ action: { type: 'play', card: m.card, mode: 'trail', take: [], target: null, value: 0 }, score: -own * 1.1 - 50 * risk - 0.3 });
    }
    for (const take of m.captures) {
      const houses = view.houses.filter((h) => h.value === seepValue(m.card));
      const prize = own + seepPointsOf(take) + houses.reduce((n, h) => n + seepPointsOf(h.cards), 0);
      const loose = gone(take);
      const left = view.houses.length - houses.length;
      let score = prize + 0.3 * (take.length + houses.length);
      if (loose.length === 0 && left === 0 && !lastPlay) score += 50;
      else score -= 50 * seepRisk(view, loose, left, ctx);
      out.push({ action: { type: 'play', card: m.card, mode: 'capture', take, target: null, value: 0 }, score });
    }
    for (const b of m.builds) {
      const target = b.target === null ? null : view.houses.find((h) => h.id === b.target)!;
      const pts = own + seepPointsOf(b.take) + (target ? seepPointsOf(target.cards) : 0);
      const risk = oppHolds(view, b.value, ctx);
      // A house is worth its cards if it survives to be captured by us, and a gift to the other side if not.
      let score = pts * (1 - 2 * risk) + 2;
      if (target && view.seat !== null && target.owner % 2 === view.seat % 2) score -= 1;
      out.push({ action: { type: 'play', card: m.card, mode: 'build', take: b.take, target: b.target, value: b.value }, score });
    }
  }
  return out;
}

export const seepBot: BotEngine<SeepView, SeepLegal, SeepAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    const all = candidates(view, legal, ctx);
    if (all.length === 0) throw new Error('No legal action');
    if (chance(ctx, ctx.profile.mistakeRate)) return all[ctx.rng.int(all.length)]!.action;
    return all.reduce((b, c) => (c.score > b.score ? c : b)).action;
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 200));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(_view, legal) {
    const cap = legal.moves.find((x) => x.captures.length > 0);
    if (cap) return { type: 'play', card: cap.card, mode: 'capture', take: cap.captures[0]!, target: null, value: 0 };
    const trail = legal.moves.find((x) => x.trail);
    if (trail) return { type: 'play', card: trail.card, mode: 'trail', take: [], target: null, value: 0 };
    const b = legal.moves.find((x) => x.builds.length > 0)!;
    const o = b.builds[0]!;
    return { type: 'play', card: b.card, mode: 'build', take: o.take, target: o.target, value: o.value };
  },
};
